import { ActionTree, GetterTree, MutationTree } from 'vuex'
import { Tag, TagCategory, UITag, UITopic } from '../../types/tag'
import { Topic } from '../../transcription.api'
import {
  mapTopicsToCategories,
  mapTopicToUI,
  TopicCategoryColors
} from '../../utils'
import { v4 as uuidv4 } from 'uuid'

export enum TagsPickerTabs {
  CUSTOM,
  ANALYSIS
}

export enum tagsTypes {
  ANALYSIS = 'Analysis',
  TOPIC = 'Topic'
}

export const tagsColorPalette = [
  'blue',
  'red',
  'green',
  'orange',
  'purple',
  'lochinvar',
  'prelude',
  'goldPurple',
  'manhattan',
  'pigPink',
  'downy',
  'grandis',
  'matterhorn',
  'winterHazel',
  'regentStBlue',
  'turquoise',
  'pueblo',
  'limeade',
  'peachCream',
  'gorse',
  'bightSun',
  'sun'
]

export function getTopicsPalette(topics: UITopic[]) {
  //Hardcoded colors for Analysis topic categories
  const colors: TopicCategoryColors = {
    entity: 'green',
    dictionary: 'orange',
    'text search': 'blue',
    question: 'red'
  }

  const leftColors = ['purple', 'lochinvar', 'prelude']

  topics.forEach(t => {
    if (!colors[t.category]) {
      colors[t.category.toLowerCase() || 'ungrouped'] =
        leftColors.pop() ?? 'gray'
    }
  })

  return colors
}

class State {
  isActive = false
  activeTab = TagsPickerTabs.CUSTOM
  openTag: UITag | null = null
  openTagIndex: number | null = null
  topicsCategories: TagCategory[] = []
  topics: UITopic[] = []
  analysisTopics: UITopic[] = []
  analysisTags: TagCategory[] = []
}

const getters = {
  isActive(state: State): boolean {
    return state.isActive
  },
  topics(state: State): UITopic[] {
    return state.topics
  },
  analysisTopics(state: State): UITopic[] {
    return state.analysisTopics
  },
  analysisActive(state: State): boolean {
    return state.activeTab === TagsPickerTabs.ANALYSIS
  },
  analysisTags(state: State): TagCategory[] {
    return state.analysisTags
  },
  topicsCategories(state: State): TagCategory[] {
    return state.topicsCategories
  },
  openTag(state: State): Tag | null {
    return state.openTag
  },
  openTagIndex(state: State): number | null {
    return state.openTagIndex
  },
  activeTab(state: State): number {
    return state.activeTab
  },
  selectedAnalysisCategories(state: State): TagCategory[] {
    return state.analysisTags
      .filter((c: TagCategory) => !!c.tags.find((t: Tag) => t.isVisible))
      .map((category: TagCategory) => {
        return {
          ...category,
          tags: category.tags.filter((tag: Tag) => tag.isVisible)
        }
      })
  },
  selectedTopics(state: State): UITopic[] {
    return state.topics.filter(t => t.isActive)
  }
} as GetterTree<State, any>

const mutations = {
  TOGGLE_ACTIVE_STATUS(state: State, status?: boolean) {
    if (status !== undefined) {
      state.isActive = status
    } else {
      state.isActive = !state.isActive
    }

    if (!state.isActive) {
      state.topicsCategories.forEach(c => (c.isOpen = false))
      state.topics.forEach((t: UITopic) => (t.isActive = false))
    }
  },
  SHOW_CUSTOM_TAGS(state: State) {
    state.activeTab = TagsPickerTabs.CUSTOM
  },
  SHOW_ANALYSIS(state: State) {
    state.activeTab = TagsPickerTabs.ANALYSIS
  },
  TOGGLE_ANALYSIS_TAG(
    state: State,
    params: { categoryId: number; tagId: number }
  ) {
    const analysisList = state.analysisTags.find(
      tag => tag.id === params.categoryId
    )
    const subtopic =
      analysisList && analysisList.tags.find(tag => tag.id === params.tagId)
    subtopic && (subtopic.isVisible = !subtopic.isVisible)
  },
  TOGGLE_TAGS_CATEGORY(state: State, id: number) {
    const category: TagCategory | undefined = state.topicsCategories.find(
      (topic: TagCategory) => topic.id === id
    )

    category && (category.isOpen = !category.isOpen)
  },
  TOGGLE_TAG(state: State, id: number) {
    const toUpdate = state.topics.find(topic => topic.id === id)
    if (!toUpdate) {
      return
    }

    toUpdate.isActive = !toUpdate.isActive
  },
  INIT_TAG_EDIT(state: State, data: { tag: UITag; index?: number }) {
    state.openTag = data.tag
    state.openTagIndex = data.index || null
  },
  CLOSE_TAG_EDIT(state: State) {
    state.openTag = null
    state.openTagIndex = null
  },
  FINISH_TAG_EDIT(state: State) {
    state.openTag = null
    state.openTagIndex = null
  },
  INIT_TOPICS(state: State, topics: Topic[]) {
    const analysisTopics = topics.filter(
      topic => topic.type === tagsTypes.ANALYSIS
    )
    const _topics = topics.filter(topic => topic.type === tagsTypes.TOPIC)

    state.topics = _topics.map(t => mapTopicToUI(t))
    state.analysisTopics = analysisTopics.map(t => mapTopicToUI(t))
  },
  SET_CATEGORIES(state: State, categories: TagCategory[]) {
    state.topicsCategories = categories
  },
  ADD_USER_DEFINED_SUBCATEGORY(state: State, subcategoryName: string) {
    const newSubtopic = {
      category: 'User defined',
      client_id: uuidv4(),
      count_required: 0,
      id: uuidv4(),
      include_in_score: true,
      isActive: false,
      project_id: uuidv4(),
      sub_category: subcategoryName,
      title: subcategoryName,
      type: 'Topic',
      roadmap: false,
      magnifier: false,
      icon: '',
      main_toggle: false,
      common_name: '',
      color: 0,
      definition: ''
    }

    state.topics.push(newSubtopic)
    const userDefinedCategory = state.topicsCategories.find(
      t => t.title.toLowerCase() === 'user defined'
    )
    state.topicsCategories.forEach(currentTopics => {
      if (userDefinedCategory) {
        if (currentTopics.id === userDefinedCategory.id) {
          currentTopics.topics.push(newSubtopic)
        }
      }
    })
  }
} as MutationTree<State>

const actions = {
  toggle(context, value?: boolean) {
    context.commit('TOGGLE_ACTIVE_STATUS', value)
  },
  addUserDefinedCategory(context, subcategoryName: string) {
    context.commit('ADD_USER_DEFINED_SUBCATEGORY', subcategoryName)
  },
  showAnalysis(context) {
    context.commit('SHOW_ANALYSIS')
  },
  showCustomTags(context) {
    context.commit('SHOW_CUSTOM_TAGS')
  },
  toggleAnalysisTag(context, params: { categoryId: number; tagId: number }) {
    context.commit('TOGGLE_ANALYSIS_TAG', params)
  },
  toggleTagsCategory(context, id: number) {
    context.commit('TOGGLE_TAGS_CATEGORY', id)
  },
  toggleTag(context, topicId) {
    context.commit('TOGGLE_TAG', topicId)
  },
  initTagEdit(context, data: { tag: Tag; index?: number }) {
    context.commit('INIT_TAG_EDIT', data)
  },
  closeTagEdit(context) {
    context.commit('CLOSE_TAG_EDIT')
  },
  finishTagEdit(context) {
    context.commit('FINISH_TAG_EDIT')
    context.commit('TOGGLE_ACTIVE_STATUS', false)
  },
  initTopics(context, topics: Topic[]) {
    context.commit('INIT_TOPICS', topics)
    const [categories, topicsColors] = mapTopicsToCategories(
      context.getters.topics
    )
    const analysisTopicsColors = getTopicsPalette(
      context.getters.analysisTopics
    )
    context.commit('SET_CATEGORIES', categories)
    return [topicsColors, analysisTopicsColors]
  }
} as ActionTree<State, any>

export default {
  namespaced: true,
  state: new State(),
  getters,
  mutations,
  actions
}
